import { NgComponentOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  signal,
  Type,
  untracked,
} from '@angular/core';

import { DynamicFormConfigService } from '../../dynamic-form-config.service';

interface UnknownItem {
  type: string;
  hash: string;
}

interface DynamicFormPresenter {
  hash: string;
  component: Type<any>;
  inputs: {
    config: object;
  };
}

@Component({
  selector: 'dynamic-form-present',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @for (presenter of presenters(); track presenter.hash) {
      <ng-template
        [ngComponentOutlet]="presenter.component"
        [ngComponentOutletInputs]="presenter.inputs"
      />
    }
  `,
  imports: [NgComponentOutlet],
  providers: [DynamicFormConfigService],
  styleUrl: './dynamic-form-present.component.less',
})
export class DynamicFormPresentComponent {
  private readonly dynamicFormAnswerService = inject(DynamicFormConfigService);

  public formItems = input.required<UnknownItem[]>();
  public presenters = signal<DynamicFormPresenter[]>([]);

  private initForm = effect(() => {
    const respondConfigs = this.formItems();
    untracked(() => this.createPresenters(respondConfigs));
  });

  private async createPresenters(items: UnknownItem[]) {
    const presenters = await Promise.all(
      items.map(async item => {
        const config = this.dynamicFormAnswerService.configsMap[item.type];
        return {
          hash: item.hash,
          component: await config.components.present(),
          inputs: {
            config: config.presentConfig(item),
          },
        };
      }),
    );

    this.presenters.set(presenters);
  }
}
